/**
 * Firestore 기반 동기화 매니저
 * 기존 SyncManager와 호환되는 인터페이스를 제공하면서
 * 내부적으로는 Firestore를 사용하여 실시간 다중 사용자 동기화 지원
 */

import { Room, Player, Question, GameState, GameSettings } from '../types/game';
import { realtimeGameSync, RealtimeGameData } from './RealtimeGameSync';
import logger from '../utils/logger';

export interface GameSyncData {
  room: Room | null;
  players: Player[];
  questions: Question[];
  gameState: GameState;
  currentQuestionIndex: number;
  gameSettings: GameSettings;
  hasStarted: boolean;
  phaseStartedAt: number | null; // ms 단위 타임스탬프
  phaseDuration: number | null; // 초 단위 (항상 초로 저장/전송)
  paused: boolean;
  pausedPrevState?: GameState | null;
  lastGradedQuestionId: string | null;
  activeQuestionId: string | null;
  lastUpdated: number;
}

class FirestoreSyncManager {
  private sessionId: string;
  private listeners: Map<string, Set<Function>> = new Map();
  private gameData: GameSyncData;
  private currentRoomCode: string | null = null;
  private lastMessageId: string = '';
  private hostActivityThrottle: Map<string, NodeJS.Timeout> = new Map(); // HOST_ACTIVITY throttle

  constructor() {
    this.sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    this.gameData = this.getInitialGameData();
    
    this.setupFirestoreListeners();
    
  }

  private getInitialGameData(): GameSyncData {
    return {
      room: null,
      players: [],
      questions: [],
      gameState: 'waiting',
      currentQuestionIndex: 0,
      gameSettings: {
        timeLimit: 30,
        answerRevealTime: 10,
        eliminationMode: false,
        eliminationThreshold: 3,
        autoMode: true,
        showCorrectAnswer: true,
        allowMultipleAttempts: false,
        randomizeQuestions: false,
        randomizeOptions: false
      },
      hasStarted: false,
      phaseStartedAt: null,
      phaseDuration: null,
      paused: false,
      pausedPrevState: null,
      lastGradedQuestionId: null,
      activeQuestionId: null,
      lastUpdated: Date.now()
    };
  }

  private setupFirestoreListeners() {
    // Firestore 실시간 동기화 리스너 설정
    const gameDataUpdateHandler = (data: RealtimeGameData) => {
      // lastMessage 처리 (다른 탭의 Toast 알림)
      if ((data as any).lastMessage) {
        const { id, message, type } = (data as any).lastMessage;
        // 새 메시지만 전달
        if (id && id !== this.lastMessageId) {
          this.lastMessageId = id;
          this.notifyListeners('SYSTEM_MESSAGE', { message, type });
        }
      }
      
      // players 객체를 배열로 변환 (최적화: 이미 배열이면 그대로 사용)
      let playersArray: any[] = [];
      
      if (data.players) {
        if (Array.isArray(data.players)) {
          // 이미 배열이면 그대로 사용 (불필요한 변환 방지)
          playersArray = data.players;
        } else if (typeof data.players === 'object') {
          // 객체인 경우에만 배열로 변환
          const playerValues = Object.values(data.players);
          playersArray = playerValues.length > 0 ? playerValues : [];
        }
      } else {
        // data.players가 없으면 기존 배열 유지
        playersArray = this.gameData.players;
      }
      
      // questions 처리: 빈 배열도 유효하므로 undefined/null만 체크
      const questions = data.questions !== undefined && data.questions !== null 
        ? data.questions 
        : this.gameData.questions;
      
      // gameSettings 기본값 처리 (기존 방의 경우 timeLimit이 5일 수 있으므로)
      const gameSettings = data.gameSettings || {};
      
      this.gameData = {
        room: data.room,
        players: playersArray, // 객체를 배열로 변환
        questions: questions,
        gameState: data.gameState,
        currentQuestionIndex: data.currentQuestionIndex,
        gameSettings: {
          timeLimit: gameSettings.timeLimit ?? 30,
          answerRevealTime: gameSettings.answerRevealTime ?? 10,
          eliminationMode: gameSettings.eliminationMode ?? false,
          eliminationThreshold: gameSettings.eliminationThreshold ?? 3,
          autoMode: gameSettings.autoMode ?? true,
          showCorrectAnswer: gameSettings.showCorrectAnswer ?? true,
          allowMultipleAttempts: gameSettings.allowMultipleAttempts ?? false,
          randomizeQuestions: gameSettings.randomizeQuestions ?? false,
          randomizeOptions: gameSettings.randomizeOptions ?? false
        },
        hasStarted: data.hasStarted,
        phaseStartedAt: data.phaseStartedAt,
        phaseDuration: data.phaseDuration,
        paused: data.paused ?? false, // paused 상태 기본값 명시적 처리
        pausedPrevState: (data as any).pausedPrevState ?? null,
        lastGradedQuestionId: (data as any).lastGradedQuestionId || null,
        activeQuestionId: (data as any).activeQuestionId || null,
        lastUpdated: data.lastUpdated
      };
      
      this.notifyListeners('GAME_DATA_UPDATE', this.gameData);
    };
    
    realtimeGameSync.addEventListener('GAME_DATA_UPDATE', gameDataUpdateHandler);
    
    // 강퇴 이벤트 전달
    realtimeGameSync.addEventListener('PLAYER_KICKED', (data: any) => {
      this.notifyListeners('PLAYER_KICKED', data);
    });

    realtimeGameSync.addEventListener('SYNC_ERROR', (error: any) => {
      console.error('🔥 Firestore 동기화 오류:', error);
      this.notifyListeners('SYNC_ERROR', error);
    });

    realtimeGameSync.addEventListener('ROOM_NOT_FOUND', () => {
      console.warn('🔥 방을 찾을 수 없음');
      // 주의: 새로고침 시 재연결을 위해 localStorage는 즉시 지우지 않음
      // 대신 재연결 시도 후에도 방이 없으면 그때 지움
      this.gameData = this.getInitialGameData();
      this.currentRoomCode = null;
      this.notifyListeners('ROOM_NOT_FOUND', null);
    });
  }

  /**
   * 방에 연결 (호스트 또는 참여자)
   */
  async connectToRoom(roomCode: string): Promise<void> {
    this.currentRoomCode = roomCode;
    await realtimeGameSync.connectToRoom(roomCode);
    
    try { 
      localStorage.setItem('currentRoomCode', roomCode);
      console.log('🔥 [SYNC] localStorage에 방 코드 저장:', roomCode);
    } catch (e) {
      console.warn('🔥 localStorage 저장 실패:', e);
    }
  }

  /**
   * 방 생성 (호스트용)
   */
  async createRoom(room: Room): Promise<void> {
    this.currentRoomCode = room.code;
    await realtimeGameSync.connectToRoom(room.code);
    
    try {
      localStorage.setItem('currentRoomCode', room.code);
      console.log('🔥 [SYNC] 방 생성 시 localStorage에 방 코드 저장:', room.code);
    } catch (e) {
      console.warn('🔥 [SYNC] localStorage 저장 실패:', e);
    }
    
    const roomData: Partial<RealtimeGameData> = {
      room,
      questions: [],
      gameSettings: this.gameData.gameSettings
    };
    
    await realtimeGameSync.createRoom(roomData);
  }

  /**
   * 플레이어 추가
   */
  async addPlayer(player: Player): Promise<void> {
    await realtimeGameSync.joinPlayer(player);
    this.notifyListeners('PLAYER_JOIN', player);
  }

  /**
   * 플레이어 제거
   */
  async removePlayer(playerId: string): Promise<void> {
    await realtimeGameSync.leavePlayer(playerId);
    this.notifyListeners('PLAYER_LEAVE', playerId);
  }

  /**
   * 플레이어 업데이트 (점수, 답안 등)
   */
  async updatePlayer(playerId: string, updates: Partial<Player>): Promise<void> {
    await realtimeGameSync.updatePlayer(playerId, updates);
  }

  /**
   * 게임 데이터 업데이트
   */
  async updateGameData(updates: Partial<GameSyncData>): Promise<void> {
    // GameSyncData를 RealtimeGameData 형식으로 변환
    // undefined 값은 제외하여 Firestore 오류 방지
    const firestoreUpdates: Partial<RealtimeGameData> = {};
    
    if (updates.room !== undefined) {
      firestoreUpdates.room = updates.room;
    }
    if (updates.questions !== undefined) {
      firestoreUpdates.questions = updates.questions;
    }
    if (updates.gameState !== undefined) {
      firestoreUpdates.gameState = updates.gameState;
    }
    if (updates.currentQuestionIndex !== undefined) {
      firestoreUpdates.currentQuestionIndex = updates.currentQuestionIndex;
    }
    if (updates.gameSettings !== undefined) {
      firestoreUpdates.gameSettings = updates.gameSettings;
    }
    if (updates.hasStarted !== undefined) {
      firestoreUpdates.hasStarted = updates.hasStarted;
    }
    if (updates.phaseStartedAt !== undefined) {
      firestoreUpdates.phaseStartedAt = updates.phaseStartedAt;
    }
    if (updates.phaseDuration !== undefined) {
      firestoreUpdates.phaseDuration = updates.phaseDuration;
    }
    if (updates.paused !== undefined) {
      firestoreUpdates.paused = updates.paused;
    }
    // 선택적 필드들도 처리 (undefined가 아닌 경우만 포함)
    if ((updates as any).pausedPrevState !== undefined) {
      firestoreUpdates.pausedPrevState = (updates as any).pausedPrevState;
    }
    if ((updates as any).lastGradedQuestionId !== undefined) {
      firestoreUpdates.lastGradedQuestionId = (updates as any).lastGradedQuestionId;
    }
    if ((updates as any).activeQuestionId !== undefined) {
      firestoreUpdates.activeQuestionId = (updates as any).activeQuestionId;
    }

    if (updates.players) {
      const playersObj: { [key: string]: Player } = {};
      updates.players.forEach(player => {
        const cleanPlayer = Object.fromEntries(
          Object.entries(player).filter(([_, v]) => v !== undefined)
        ) as Player;
        playersObj[player.id] = cleanPlayer;
      });
      firestoreUpdates.players = playersObj;
    }

    try {
      await realtimeGameSync.updateGameState(firestoreUpdates);
    } catch (error) {
      console.error('[SYNC] realtimeGameSync.updateGameState 실패:', error);
      throw error;
    }
  }

  /**
   * 배치 플레이어 업데이트 (성능 최적화)
   */
  async batchUpdatePlayers(playerUpdates: { [playerId: string]: Partial<Player> }): Promise<void> {
    await realtimeGameSync.batchUpdatePlayers(playerUpdates);
  }

  /**
   * 플레이어 강퇴
   */
  async kickPlayer(playerId: string): Promise<void> {
    await realtimeGameSync.kickPlayer(playerId);
  }

  /**
   * 플레이어 나가기
   */
  async leavePlayer(playerId: string): Promise<void> {
    await realtimeGameSync.leavePlayer(playerId);
  }

  /**
   * 현재 게임 데이터 가져오기
   */
  getGameData(): GameSyncData {
    return { ...this.gameData };
  }

  /**
   * 이벤트 리스너 등록
   */
  addEventListener(eventType: string, callback: Function): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);
  }

  /**
   * 이벤트 리스너 제거
   */
  removeEventListener(eventType: string, callback: Function): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * 리스너들에게 알림
   */
  private notifyListeners(eventType: string, data: any): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('🔥 리스너 콜백 오류:', error);
        }
      });
    }
  }

  /**
   * 방 삭제
   */
  async deleteRoom(roomCode?: string): Promise<void> {
    const targetRoomCode = roomCode || this.currentRoomCode;
    if (!targetRoomCode) {
      logger.warn('🔥 방 삭제 실패: 방 코드가 없음');
      return;
    }
    
    try {
      // roomCode를 명시적으로 전달하여 삭제
      if (roomCode) {
        // 직접 방 코드를 전달하는 경우 (새로운 연결 없이 삭제)
        const { doc, deleteDoc } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');
        const roomRef = doc(db, 'game_rooms', roomCode);
        await deleteDoc(roomRef);
        logger.info('🔥 방 삭제 완료 (직접):', roomCode);
      } else {
        // 현재 연결된 방 삭제
        await realtimeGameSync.deleteRoom();
        logger.info('🔥 방 삭제 완료 (연결된 방):', targetRoomCode);
      }
      
      // 상태 정리
      if (this.currentRoomCode === targetRoomCode) {
        this.currentRoomCode = null;
        this.gameData = this.getInitialGameData();
        try { localStorage.removeItem('currentRoomCode'); } catch {}
      }
    } catch (error) {
      logger.error('🔥 방 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 연결 해제
   */
  disconnect(): void {
    realtimeGameSync.disconnect();
    this.currentRoomCode = null;
    this.gameData = this.getInitialGameData();
    try { localStorage.removeItem('currentRoomCode'); } catch {}
  }

  /**
   * 연결 상태 확인
   */
  isConnected(): boolean {
    return realtimeGameSync.isConnected();
  }

  /**
   * 현재 방 코드 가져오기
   */
  getCurrentRoomCode(): string | null {
    return this.currentRoomCode;
  }

  /**
   * 하위 호환성을 위한 메서드들
   */
  async broadcast(eventType: string, data: any): Promise<void> {
    // HOST_ACTIVITY는 Firestore에 직접 업데이트
    if (eventType === 'HOST_ACTIVITY') {
      const roomCode = data.roomCode;
      if (!roomCode) return;
      
      // throttle 체크 (최대 10초에 한 번)
      const key = roomCode || 'default';
      const existingTimeout = this.hostActivityThrottle.get(key);
      
      if (existingTimeout) {
        // 이미 예약된 업데이트가 있으면 무시 (throttle)
        return;
      }
      
      // Firestore에 lastHostActivity 업데이트
      try {
        await realtimeGameSync.ping();
      } catch (e) {
        // 실패 시 무시 (에러 로깅 필요시 추가)
      }
      
      // 10초 후 throttle 해제
      const timeout = setTimeout(() => {
        this.hostActivityThrottle.delete(key);
      }, 10000);
      
      this.hostActivityThrottle.set(key, timeout);
      return;
    }
    
    // SYSTEM_MESSAGE는 Firestore를 통해서만 전달
    if (eventType === 'SYSTEM_MESSAGE') {
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const messageData = {
        id: messageId,
        message: data.message,
        type: data.type,
        timestamp: Date.now()
      };
      
      realtimeGameSync.updateGameState({ 
        lastMessage: messageData
      } as any).catch(error => {
        console.warn('🔥 메시지 브로드캐스트 실패:', error);
        this.notifyListeners('SYSTEM_MESSAGE', messageData);
      });
    } else {
      this.notifyListeners(eventType, data);
    }
    
  }

  private heartbeatInterval: NodeJS.Timeout | null = null;

  startHeartbeat(): void {
    // 기존 heartbeat가 있으면 정리
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    // 5초마다 ping으로 lastUpdated만 갱신하여 방 만료 방지
    const ping = async () => {
      try { await realtimeGameSync.ping(); } catch {}
    };
    ping();
    this.heartbeatInterval = setInterval(ping, 5000);
  }

  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  updateGameState(changes: Partial<GameSyncData>): void {
    // 비동기 버전으로 리다이렉트
    this.updateGameData(changes).catch(error => {
      console.error('🔥 게임 상태 업데이트 오류:', error);
    });
  }
}

// 싱글톤 인스턴스 (기존 syncManager와 호환)
export const firestoreSyncManager = new FirestoreSyncManager();
